import { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { mapOutline, openOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { buildMapsUrl, type MapsDestination } from '../../utils/maps';

interface OpenInMapsButtonProps {
  /** Where to drop the pin. Renders nothing if it has neither street nor coords. */
  destination: MapsDestination;
  /** Names the pin when `destination` falls back to coordinates. */
  label?: string;
  /** Extra classes on the trigger button. */
  className?: string;
}

/**
 * "Open in Maps" plus the Apple/Google picker it opens. Same two choices on
 * every platform — see `utils/maps` for why we can't read the OS preference or
 * detect which apps are installed.
 *
 * The picker rows are real anchors, not programmatic navigation: iOS Safari
 * and standalone PWAs block `window.open` outside a direct gesture on a link,
 * and navigating the current tab would strand the user on a web map inside the
 * app shell when the native app isn't installed.
 *
 * Callers are responsible for only rendering this against a revealed address —
 * never an obfuscated one.
 */
export const OpenInMapsButton: React.FC<OpenInMapsButtonProps> = ({
  destination,
  label,
  className,
}) => {
  const { t } = useTranslation('orders');
  const [pickerOpen, setPickerOpen] = useState(false);

  const appleUrl = buildMapsUrl('apple', destination, label);
  const googleUrl = buildMapsUrl('google', destination, label);
  if (!appleUrl || !googleUrl) return null;

  const rowClass =
    'flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3 text-sm font-semibold text-foreground active:opacity-80';

  return (
    <>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className={`flex w-full items-center justify-center gap-2 rounded-xl btn-outline border-brand-primary px-4 py-2.5 text-sm font-semibold text-brand-primary active:opacity-80 ${className ?? ''}`}
      >
        <IonIcon icon={mapOutline} className="text-lg" />
        <span>{t('maps.openInMaps')}</span>
      </button>

      <Modal isOpen={pickerOpen} onClose={() => setPickerOpen(false)} className="space-y-4">
        <p className="pr-8 text-base font-semibold">{t('maps.pickerTitle')}</p>
        <div className="space-y-2">
          <a
            href={appleUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setPickerOpen(false)}
            className={rowClass}
          >
            <span>{t('maps.apple')}</span>
            <IonIcon icon={openOutline} className="shrink-0 text-lg text-muted-foreground" />
          </a>
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setPickerOpen(false)}
            className={rowClass}
          >
            <span>{t('maps.google')}</span>
            <IonIcon icon={openOutline} className="shrink-0 text-lg text-muted-foreground" />
          </a>
        </div>
      </Modal>
    </>
  );
};
